import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NavigationDashboardTabItemDto } from '@asset-metrix/portal-gateway-client-js';

const DASHBOARD_PARAM = 'dashboard';

function writeDashboardSlug(
  searchParams: URLSearchParams,
  slug: string | null
) {
  if (slug == null) {
    searchParams.delete(DASHBOARD_PARAM);
  } else {
    searchParams.set(DASHBOARD_PARAM, slug);
  }
}

/**
 * Maps `?dashboard=` to a pinned tab slug.
 * Accepts a slug, or a dashboard UUID from older URLs, then callers rewrite to the slug.
 */
export function resolveDashboardSlugFromUrlValue(
  raw: string | null,
  dashboardNavItems: readonly NavigationDashboardTabItemDto[]
): string | undefined {
  if (raw == null) {
    return undefined;
  }

  const bySlug = dashboardNavItems.find((item) => item.slug === raw);
  if (bySlug != null) {
    return bySlug.slug;
  }

  return dashboardNavItems.find((item) => item.dashboardId === raw)?.slug;
}

export function useDashboardUrlState(
  topNavigationItems: readonly NavigationDashboardTabItemDto[]
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawUrlValue = searchParams.get(DASHBOARD_PARAM);

  const firstInstanceSlug = topNavigationItems[0]?.slug ?? null;
  const resolvedSlugFromUrl = resolveDashboardSlugFromUrlValue(
    rawUrlValue,
    topNavigationItems
  );
  const activeDashboardSlug = resolvedSlugFromUrl ?? firstInstanceSlug;

  const activeDashboardId =
    activeDashboardSlug == null
      ? null
      : (topNavigationItems.find((item) => item.slug === activeDashboardSlug)
          ?.dashboardId ?? null);

  const setActiveDashboardSlug = useCallback(
    (slug: string | null, replace = false) => {
      const next = new URLSearchParams(searchParams);
      writeDashboardSlug(next, slug);
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams]
  );

  const setActiveDashboardById = useCallback(
    (dashboardId: string | null, replace = false) => {
      if (dashboardId == null) {
        setActiveDashboardSlug(null, replace);
        return;
      }

      const slug = topNavigationItems.find(
        (item) => item.dashboardId === dashboardId
      )?.slug;
      if (slug != null) {
        setActiveDashboardSlug(slug, replace);
      }
    },
    [setActiveDashboardSlug, topNavigationItems]
  );

  useEffect(() => {
    if (activeDashboardSlug == null || topNavigationItems.length === 0) {
      return;
    }

    const alreadyCanonical =
      resolvedSlugFromUrl != null &&
      searchParams.get(DASHBOARD_PARAM) === activeDashboardSlug;
    if (alreadyCanonical) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    writeDashboardSlug(next, activeDashboardSlug);
    setSearchParams(next, { replace: true });
  }, [
    activeDashboardSlug,
    resolvedSlugFromUrl,
    searchParams,
    setSearchParams,
    topNavigationItems.length,
  ]);

  return {
    activeDashboardId,
    activeDashboardSlug,
    setActiveDashboardById,
    setActiveDashboardSlug,
  };
}
